import React, { useState } from 'react';
import { Filter } from 'lucide-react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { formatDateShort } from '../../utils/dateFormat';

// Presentational — a sub-tab of Participation Records: the rows arrive
// already run through the shared filters (search / session / date range)
// owned by ParticipationRecords, which also fetches them and the
// managed Absence Reason list passed in as `reasonList`. The reason
// filter stays here — it is the one filter the other two sub-tabs have no use
// for — as do the breakdown and the evidence counts, which are the reason
// this view exists.

const AbsenceReasons = ({ records, reasonList }) => {
  const [filterReason, setFilterReason] = useState('all');

  // Filter options: the maintained list first, then any category on a record
  // that has since left it (a deactivated option still has history behind it).
  const usedReasons = [...new Set(records.map(a => a.reasonCategory).filter(Boolean))];
  const uniqueReasons = ['all', ...reasonList, ...usedReasons.filter(r => !reasonList.includes(r))];

  const filteredAbsences = records.filter(
    record => filterReason === 'all' || record.reasonCategory === filterReason
  );

  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(filteredAbsences);

  // Calculate statistics
  const totalAbsences = filteredAbsences.length;
  const reasonCounts = filteredAbsences.reduce((acc, record) => {
    const reason = record.reasonCategory || 'Unknown';
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});
  // Biggest category first — the breakdown is the reason this tab exists.
  const rankedReasons = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
  const topReason = rankedReasons[0] || null;
  const withEvidence = filteredAbsences.filter(r => r.evidence === true).length;
  const withoutEvidence = filteredAbsences.filter(r => r.evidence === false).length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Absences</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{totalAbsences}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Most Common Reason</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{topReason ? topReason[0] : '—'}</p>
          {topReason && (
            <p className="text-xs text-gray-500 mt-1">
              {topReason[1]} of {totalAbsences} ({((topReason[1] / totalAbsences) * 100).toFixed(1)}%)
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">With Evidence</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{withEvidence}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Without Evidence</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{withoutEvidence}</p>
        </div>
      </div>

      {/* Reason Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Absence Reasons Breakdown</h3>
        {rankedReasons.length === 0 ? (
          <p className="text-sm text-gray-500">No absence records to break down.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rankedReasons.map(([reason, count]) => (
              <div key={reason} className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">{reason}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {((count / totalAbsences) * 100).toFixed(1)}%
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reason filter — the one filter specific to this sub-tab */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative sm:max-w-sm">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            value={filterReason}
            onChange={(e) => setFilterReason(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
          >
            {uniqueReasons.map(reason => (
              <option key={reason} value={reason}>
                {reason === 'all' ? 'All Reasons' : reason}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Absence Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Training Session" sortKey="courseTitle" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Session Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Reason" sortKey="reasonCategory" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Evidence" sortKey="evidence" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Updated At" sortKey="updatedAt" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Remarks" sortKey="remarks" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAbsences.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No absence records found
                  </td>
                </tr>
              ) : (
                rows.map((record, index) => {
                  const hasEvidence = record.evidence === true;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {record.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{record.courseTitle}</div>
                        <div className="text-xs text-gray-500">ID: {record.sessionCode}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {record.date ? formatDateShort(record.date) : '—'}
                        </div>
                      </td>
                      {/* The categories are maintained in Dropdown Lists, so no
                          colour can be tied to a particular value — one badge
                          for the classification, the person's own words below. */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {record.reasonCategory}
                        </span>
                        {record.reasonDetail && (
                          <div className="text-xs text-gray-500 mt-1 max-w-xs">{record.reasonDetail}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          hasEvidence ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {hasEvidence ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateShort(record.updatedAt)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(record.updatedAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{record.remarks || '-'}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default AbsenceReasons;
